/**
 * Where a viewer gets a document's bytes. One place, so every renderer reads the current file
 * and older versions the same way:
 *  - current file: the storage URL with a fresh SAS token (getDocumentBlobUrl)
 *  - older version (`viewRevisionId`): its own file URL (`viewStorageReference`) with a fresh SAS
 *    token, falling back to the bearer-auth API stream; cached as a blob: URL by documentProxyUtils.
 *    That cache owns those URLs, so nothing here revokes them.
 */
import { getDocumentBlobUrl, getDocumentId, downloadDocumentVersion } from '@/utils/documentProxyUtils';

export class DocumentSourceError extends Error {
  constructor(message) {
    super(message);
    this.name = 'DocumentSourceError';
  }
}

export function isAbortError(err) {
  return err?.name === 'AbortError';
}

function statusMessage(status) {
  if (status === 403) return 'You do not have access to this file, or its link has expired. Reload and try again.';
  if (status === 404) return 'The file was not found in storage. It may have been moved or deleted.';
  return `The file could not be loaded (HTTP ${status}).`;
}

/** A URL the browser can load directly (img, video, fetch). */
export async function resolveDocumentUrl(document) {
  const url = await getDocumentBlobUrl(document, { strict: true });
  if (!url) {
    throw new DocumentSourceError(
      document?.viewRevisionId ? 'This version could not be loaded.' : 'This document has no file to show.',
    );
  }
  // The `#file.ext` hint is for the Forge viewer; nothing here needs it.
  return url.split('#')[0];
}

/** The whole file as a Blob. */
export async function fetchDocumentBlob(document, { signal } = {}) {
  const url = await resolveDocumentUrl(document);
  let response;
  try {
    response = await fetch(url, { signal });
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new DocumentSourceError('The file could not be downloaded. Check your connection and try again.');
  }
  if (!response.ok) throw new DocumentSourceError(statusMessage(response.status));
  return response.blob();
}

/**
 * The file as text, cut at `maxBytes`.
 * @returns {Promise<{ text: string, truncated: boolean, size: number }>}
 */
export async function readDocumentText(document, maxBytes, { signal } = {}) {
  const blob = await fetchDocumentBlob(document, { signal });
  const truncated = blob.size > maxBytes;
  const text = await (truncated ? blob.slice(0, maxBytes) : blob).text();
  return { text, truncated, size: blob.size };
}

/** Save the shown file (current or older version) under its file name. Resolves to success. */
export async function downloadDocument(document) {
  const fileName = document?.fileName || document?.documentNumber || 'document';
  if (document?.viewRevisionId) {
    return downloadDocumentVersion(getDocumentId(document), document.viewRevisionId, fileName, document.viewStorageReference);
  }
  try {
    // Cross-origin links ignore `download`, so save a local copy instead.
    const blob = await fetchDocumentBlob(document);
    const objectUrl = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    return true;
  } catch (err) {
    return false;
  }
}

/** The facility route that shows a document on its own page, or null when it has none. */
export function standaloneDocumentRoute(document, facilityId) {
  const documentId = getDocumentId(document);
  if (!documentId || !facilityId || document?.viewRevisionId || document?._isTemporary) return null;
  return { name: 'facility-document', params: { id: facilityId, documentId } };
}
